import { ExportOutlined, InfoCircleOutlined } from "@ant-design/icons";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Grid, IconButton, Pagination, Button as UploadButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker as AntDate, Button, Checkbox, Col, Divider, Modal, Row, Select, Table, Tooltip, Typography, message, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import qs from "query-string";
import React, { useEffect, useState } from "react";
import AuthService from "../../Components/auth/AuthService";
import { extractOriginalFileName, formatCurrency, getNewFileName } from "../../Components/common/CommonFun";
import { DATE_FORMAT, DEFAULT_DATE_FORMAT } from "../../Components/common/constants";
import Message from "../../Components/common/Message";
import { domain, env, netsuite_url, tms_uploads_bucket, tms_uploads_cdn } from "../../Components/config";
import { showMessage } from "../../Components/Utils/message";
import { hideLoading, showLoading, useLoadingDispatch } from "../../context/loadingContext";
import "./reports.css";

const { RangePicker } = AntDate;
const { Option } = Select;

const Auth = new AuthService();

const useStyles = makeStyles((theme) => ({
  dataSheet: {
    padding: "15px",
    background: "white",
    "& .ant-card .ant-card-body": {
      padding: "4px",
      backgroundColor: "rgb(240, 240, 240)",
    },
    "& .ant-statistic .ant-statistic-content": {
      fontSize: "12px",
      fontWeight: "bold",
    },
    "& .ant-statistic .ant-statistic-title": {
      fontSize: "12px",
    },
    "& .example-custom-input": {
      width: "100% !important",
    },
    "& .react-datepicker-wrapper": {
      width: "100% !important",
    },
  },
}));

const CreditReport = () => {
  const classes = useStyles();
  var layoutDispatch = useLoadingDispatch();
  const [dateRange, setDateRange] = useState(null);
  const [loadMessage, setLoadMessage] = useState("");
  const [type, setType] = useState("");
  const [reportData, setReportData] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [page, setPage] = React.useState(1);
  const [pageChange, setPageChange] = React.useState(false);
  const [limit] = React.useState(50);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [returnUpdate, setReturnUpdate] = useState({
    status: null,
    department: null,
    reason: null,
    notes: '',
    resolved: false
  });
  const [files, setFiles] = useState([]);
  const [currentItemFiles, setCurrentItemFiles] = useState([]);

  const getUploadUrl = async (file, id) => {
    const params = {
      bucket: tms_uploads_bucket,
      ContentType: file.file.type,
      Key: env + "/unpaid-orders/" + id + "/" + file.name,
    };
    const response = await fetch(
      `${domain}/generate-put-url?${qs.stringify(params)}`,
      {
        headers: {
          "Content-Type": params.ContentType,
        },
      }
    );
    return response.json();
  };

  const uploadFileToS3 = async (uploadUrl, file, contentType) => {
    const response = await fetch(uploadUrl.putURL, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType,
      },
    });
    if (response.status !== 200) {
      throw new Error("File upload failed.");
    }
  };

  const handleUpdateStatus = async () => {
    showLoading(layoutDispatch);
    try {
      const res = await Auth.fetch("/report/unpaid-orders-status", {
        method: "POST",
        body: JSON.stringify({
          internal_id: selectedItem?.internal_id,
          credit_control_status: returnUpdate?.resolved ? 1 : null,
          credit_control_notes: returnUpdate?.notes,
          credit_control_files: currentItemFiles,
        }),
      });
      if (res?.ack) {
        showMessage('success', res?.message || 'Status updated successfully');

        const uploadPromises = [];
        if (files.length > 0) {
          files.forEach((f) => {
            uploadPromises.push(
              getUploadUrl(f, selectedItem?.internal_id)
                .then(uploadUrl => uploadFileToS3(uploadUrl, f.file, f.file.type))
                .catch(error => console.error("Error uploading file:", error))
            );
          });
        }
        await Promise.all(uploadPromises);

        setReportData((prevData) =>
          prevData.map((item) =>
            item.internal_id === selectedItem?.internal_id
              ? {
                  ...item,
                  credit_control_status: returnUpdate?.resolved ? 1 : null,
                  credit_control_notes: returnUpdate?.notes,
                  credit_control_files: JSON.stringify(currentItemFiles || []),
                }
              : item
          )
        );

        setSelectedItem(null);
        setReturnUpdate({
          status: null,
          department: null,
          reason: null,
          notes: ''
        });
        setFiles([]);
        setCurrentItemFiles([]);
        setModalOpen(false);
        hideLoading(layoutDispatch);
      } else {
        showMessage('error', res?.message);
        hideLoading(layoutDispatch);
      }

    } catch (err) {
      console.error("API Error:", err);
      showMessage('error', 'something went wrong');
      hideLoading(layoutDispatch);
    }
  };

  const cancelModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setReturnUpdate({
      status: null,
      department: null,
      reason: null,
      notes: ''
    });
    setFiles([]);
    setCurrentItemFiles([]);
  };

  const deleteNormalFiles = (fileName) => {
    const updatedFiles = files?.filter((file) => file.name !== fileName);
    setFiles(updatedFiles);
    setCurrentItemFiles(currentItemFiles?.filter((item) => item !== fileName));
  };

  const handleDeleteCurrentFile = (itemToDelete) => {
    setCurrentItemFiles(
      currentItemFiles.filter((item) => item !== itemToDelete)
    );
  };

  const handleSubmitImage = (e) => {
    e.preventDefault();
    let acceptedFiles = e.target.files;

    for (let file of acceptedFiles) {
      const newFileName = getNewFileName(file.name);
      setLoadMessage("");
      setFiles((prevFiles) => [
        ...prevFiles,
        {
          name: newFileName,
          display_name: newFileName,
          file: file,
          ContentType: file.type,
        },
      ]);
      setCurrentItemFiles((prev) => [...prev, newFileName]);
    }
  };

  const getCreditReport = async (download) => {
    let search = {
      page: page,
      show_resolved: showResolved ? 1 : 0
    };
    
    if (searchText) search.searchText = searchText;
    if (dateRange && dateRange[0]) search.start_date = dateRange[0].format(DEFAULT_DATE_FORMAT);
    if (dateRange && dateRange[1]) search.end_date = dateRange[1].format(DEFAULT_DATE_FORMAT);
    if (download) search.download = 1;

    showLoading(layoutDispatch);
    try {
      const res = await Auth.fetch("/report/unpaid-orders", {
        method: "POST",
        body: JSON.stringify(search),
      });
      if (res?.ack) {
        if (download === 1) {
          window.open(res.file_url, "_blank", "noreferrer");
        } else {
          setReportData(res?.unpaid_orders || []);
          setTotalPages(Math.ceil((res?.total_count || 0) / limit));
          setTotalCount(res?.total_count || 0);
        }
      } else {
        setReportData([]);
        message.error(res?.message || "Failed to fetch data");
      }
    } catch (err) {
      console.log(err);
      message.error(err?.message || "Something went wrong");
    } finally {
      hideLoading(layoutDispatch);
    }
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => getCreditReport(), 1000);
    return () => clearTimeout(timeOutId);
  }, [dateRange, page, showResolved, searchText]);

  const handleChange = (event, value) => {
    setPage(value);
    setPageChange(true);
  };

  const detailsCol = [
    {
      title: "Date",
      render: (record) => (
        <span>{record?.delivery_date ? dayjs(record.delivery_date).format(DATE_FORMAT) : '-'}</span>
      ),
    },
    {
      title: "Order",
      render(record) {
        return (
          <span>
            {record?.internal_id ? (
              <a
                href={`${netsuite_url}/app/accounting/transactions/salesord.nl?id=${record.internal_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record?.sale_order_no}
              </a>
            ) : (
              record?.sale_order_no || '-'
            )}
          </span>
        );
      },
    },
    {
      title: "Invoice",
      render(record) {
        return (
          <span>
            {record?.inv_internal_id ? (
              <a
                href={`${netsuite_url}/app/accounting/transactions/custinvc.nl?id=${record.inv_internal_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record?.invoice_no}
              </a>
            ) : (
              record?.invoice_no || '-'
            )}
          </span>
        );
      },
    },
    {
      title: "Customer",
      render: (record) => {
        const customerText = `${record?.customer_name ? `[${record.customer_name}]` : ''} ${record?.customer_project_id ? `${record.customer_project_id}` : ''}`.trim();
        return (
          <Tooltip title={customerText}>
            {record?.customer_internal_id ? (
              <Typography.Link 
                href={`${netsuite_url}/app/common/entity/custjob.nl?id=${record.customer_internal_id}`} 
                target="_blank" 
                style={{ maxWidth: 200 }} 
                ellipsis={true}
              >
                {customerText}
              </Typography.Link>
            ) : (
              <Typography.Text style={{ maxWidth: 200 }} ellipsis={true}>
                {customerText}
              </Typography.Text>
            )}
          </Tooltip>
        );
      },
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
    },
    {
      title: "Route",
      dataIndex: "route",
      key: "route",
    },
    {
      title: "Terms",
      dataIndex: "terms",
      key: "terms",
    },
    {
      title: "Order Value",
      render: (record) => <span>{formatCurrency(record?.delivered_order_value)}</span>,
    },
    {
      title: "Cash Received",
      render: (record) => <span>{formatCurrency(record?.amount_received_cash)}</span>,
    },
    {
      title: "Card Received",
      render: (record) => <span>{formatCurrency(record?.amount_received_card)}</span>,
    },
    {
      title: "Weight",
      render: (record) => {
        const weight = Number(record?.order_weight || 0);
        return <span>{weight.toFixed(2)} KGs</span>;
      },
    },
    {
      title: "Status",
      render: (record) => {
        let statusText;
        let backgroundColor;

        const delivered_at = record?.delivered_at && dayjs(record.delivered_at).isValid()
          ? dayjs(record.delivered_at).format(`${DATE_FORMAT} HH:mm:ss`)
          : "N/A";

        switch (record?.delivered) {
          case 1:
            statusText = "Delivered";
            backgroundColor = "#80cb80";
            break;
          case 2:
            statusText = "Postponed";
            backgroundColor = "#f0d787";
            break;
          case -1:
            statusText = "Submitted";
            backgroundColor = "#1890FF";
            break;
          default:
            statusText = "Not Delivered";
            backgroundColor = "#f94058ff";
            break;
        }

        return {
          props: {
            style: {
              background: backgroundColor,
              textAlign: 'center',
            },
          },
          children: (
            <>
              {statusText}
              {record?.delivered === 1 && (
                <Tooltip title={`Delivered at: ${delivered_at}`}>
                  <InfoCircleOutlined style={{ marginLeft: 8, color: "#000" }} />
                </Tooltip>
              )}
              {record?.delivered === 2 && record?.postponed_notes && (
                <Tooltip title={`Postponed Notes: ${record.postponed_notes}`}>
                  <InfoCircleOutlined style={{ marginLeft: 8, color: "#000" }} />
                </Tooltip>
              )}
            </>
          ),
        };
      },
    },
    {
      title: "Approved By",
      dataIndex: "approved_by",
      key: "approved_by",
    },
    {
      title: "Approval Notes",
      dataIndex: "payment_req_notes",
      key: "payment_req_notes",
      render: (text) => (
          <Typography.Paragraph
              style={{ maxWidth: 200, margin: 0 }}
              ellipsis={{
                  rows: 1,
                  expandable: 'collapsible',
                  symbol: (expanded) => (expanded ? 'less' : 'more'),
              }}
          >
              {text}
          </Typography.Paragraph>
      ),
    },
    {
      title: "Resolved By",
      dataIndex: "resolved_by",
      key: "resolved_by",
    },
    {
      title: "Resolution Notes",
      dataIndex: "credit_control_notes",
      key: "credit_control_notes",
      render: (text) => (
          <Typography.Paragraph
              style={{ maxWidth: 200, margin: 0 }}
              ellipsis={{
                  rows: 1,
                  expandable: 'collapsible',
                  symbol: (expanded) => (expanded ? 'less' : 'more'),
              }}
          >
              {text}
          </Typography.Paragraph>
      ),
    },
    {
      title: "Action",
      align: "center",
      render: (record) => {
        return (
          <ExportOutlined
            className="animated-select-icon"
            style={{ fontSize: 22, cursor: "pointer" }}
            onClick={() => {
              setSelectedItem(record);
              setReturnUpdate({
                  notes: record.credit_control_notes || '',
                  resolved: record.credit_control_status === 1
                });
                let img = [];
                try {
                  img = record?.credit_control_files ? JSON.parse(record.credit_control_files) : [];
                } catch (e) {
                  img = [];
                }
                setCurrentItemFiles(img || []);
                setFiles([]);
                setModalOpen(true);
              }}
            />
        );
      },
    },
  ];

  const updateFilters = (newValues) => {
    const existingFiltersString = localStorage.getItem('CreditReportFilters');
    let existingFilters = {};
    if (existingFiltersString) {
      try {
        existingFilters = JSON.parse(existingFiltersString);
      } catch (e) {
        console.error("Error parsing CreditReportFilters from localStorage", e);
      }
    }
    Object.assign(existingFilters, newValues);
    existingFilters.timestamp = new Date().getTime();
    const updatedFiltersString = JSON.stringify(existingFilters);
    localStorage.setItem('CreditReportFilters', updatedFiltersString);
  };

  const getFiltersOrDefault = () => {
    const existingFiltersString = localStorage.getItem('CreditReportFilters');
    let existingFilters = {};
    if (existingFiltersString) {
      try {
        existingFilters = JSON.parse(existingFiltersString);
      } catch (e) {
        console.error("Error parsing CreditReportFilters from localStorage", e);
      }
    }
    const currentTime = new Date().getTime();
    const expirationTime = 30 * 60 * 1000;
    if (existingFilters.timestamp && currentTime - existingFilters.timestamp < expirationTime) {
      return existingFilters;
    } else {
      localStorage.removeItem('CreditReportFilters');
      return {};
    }
  };

  useEffect(() => {
    const initialState = getFiltersOrDefault();

    if (initialState?.dateRange && initialState.dateRange[0]) {
      setDateRange([dayjs(initialState.dateRange[0]), dayjs(initialState.dateRange[1])]);
    } else {
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      const lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
      setDateRange([dayjs(firstDayOfMonth), dayjs(lastDayOfMonth)]);
      initialState.dateRange = [dayjs(firstDayOfMonth).format(DEFAULT_DATE_FORMAT), dayjs(lastDayOfMonth).format(DEFAULT_DATE_FORMAT)];
    }

    if (initialState?.showResolved !== undefined) {
      setShowResolved(initialState.showResolved);
    }
    
    if (initialState?.searchText !== undefined) {
      setSearchText(initialState.searchText);
    }

    initialState.timestamp = new Date().getTime();
    updateFilters(initialState);
  }, []);

  useEffect(() => {
    message.config({
      top: window.innerHeight - 100,
      duration: 3,
    });
  }, []);

  return (
    <div className={classes.dataSheet}>
      <div style={{ marginBottom: "10px", display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Credit Report</h3>
        {reportData?.length > 0 && (
          <div>
            <Button type="primary" onClick={() => { getCreditReport(1) }}>
              Download
            </Button>
          </div>
        )}
      </div>

      <div className="view-page">
        <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: "20px" }}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontWeight: 500, marginBottom: "8px" }}>Search:</label>
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  updateFilters({ searchText: e.target.value });
                  setPageChange(false);
                  setPage(1);
                }}
                allowClear
                size="large"
                style={{ width: '100%' }}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={5} lg={4} xl={3}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label htmlFor="pcnDate" style={{ fontWeight: 500, marginBottom: "8px" }}>Date:</label>
              <RangePicker
                format={DATE_FORMAT}
                style={{ width: '100%' }}
                size="large"
                value={dateRange}
                onChange={(dates) => {
                  let dRange = dates;
                  setDateRange(dRange);
                  updateFilters({
                    dateRange: dRange ? [dRange[0].format(DEFAULT_DATE_FORMAT), dRange[1].format(DEFAULT_DATE_FORMAT)] : null,
                  });
                  setPageChange(false);
                  setPage(1);
                }}
                allowClear={true}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} lg={5} xl={6}>
            <div style={{ display: "flex", gap: "20px", alignItems: "center", height: "40px" }}>
              <Checkbox
                checked={showResolved}
                onChange={(e) => {
                  setShowResolved(e.target.checked);
                  updateFilters({ showResolved: e.target.checked });
                  setPageChange(false);
                  setPage(1);
                }}
              >
                Show Resolved
              </Checkbox>
            </div>
          </Grid>
        </Grid>
        
        <Table
          style={{ marginTop: "10px" }}
          scroll={{ x: 'max-content' }}
          columns={detailsCol}
          dataSource={reportData}
          pagination={false}
          footer={() => {
            const startRecord = (page - 1) * limit + 1;
            const endRecord = Math.min(page * limit, reportData?.length ? totalCount : 0);
            return `Showing ${startRecord}-${endRecord} of ${reportData?.length ? totalCount : "0"} Records`;
          }}
          rowClassName={(record) => {
            if (record?.credit_control_status === 1) return 'light-green-row';
            if (record?.credit_control_status === null) return 'light-orange-row';
            return '';
          }}
        />
        
        <div style={{ float: "right", margin: "10px" }}>
          <Pagination
            page={page}
            count={totalPages}
            variant="outlined"
            color="primary"
            onChange={handleChange}
          />
        </div>
      </div>
      <Message type={type} msg={loadMessage} />

      <Modal open={modalOpen} title="Credit Control Status" onCancel={cancelModal} onOk={handleUpdateStatus} okText="Update" width={'70vw'}>
        <Divider style={{ margin: '2px 2px' }} />

        <Checkbox style={{ marginTop: '10px', marginLeft: '15px',  scale: '1.2' }}
          checked={returnUpdate?.resolved}
          onChange={(e) => {
            setReturnUpdate((prev) => ({
              ...prev,
              resolved: e.target.checked
            }))
          }
          }
        > Mark as Resolved
        </Checkbox>
        <Divider style={{ margin: '10px 2px' }} />


        <div>
          <UploadButton
            component="label"
            variant="contained"
            sx={{ marginTop: "10px" }}
            startIcon={<CloudUploadIcon />}
          >
            Add files
            <input
              type="file"
              hidden
              multiple
              capture
              onChange={(e) => {
                const myfiles = e.target.files;

                if (myfiles.length > 10) {
                  message.error("A maximum of 10 images can be uploaded at a time.");
                  e.target.value = "";
                } else {
                  handleSubmitImage(e);
                }
              }}
            />
          </UploadButton>
        </div>

        {currentItemFiles?.length > 0 && <h4>Old Files:</h4>}
        <ul>
          {currentItemFiles?.length > 0 &&
            currentItemFiles
              .filter(
                (item) =>
                  !files.some((file) => file.name === item)
              )
              .map((item) => {

                const encodedFileName = item.replace(/\+/g, '%2B');

                return (
                  <li>
                    <IconButton
                      aria-label="delete"
                      color="primary"
                      onClick={() => handleDeleteCurrentFile(item)}
                    >
                      <DeleteIcon />
                    </IconButton>


                    <a
                      href={`${tms_uploads_cdn}/${env}/unpaid-orders/${selectedItem?.internal_id}/${item}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {extractOriginalFileName(encodedFileName)}
                    </a>
                  </li>
                );
              })}
        </ul>
        <Divider />
        {files?.length > 0 && <h4>New Files:</h4>}
        <ul>
          {files.length > 0 &&
            files.map((fileObj) => {
              const encodedFileName = fileObj?.name.replace(/\+/g, '%2B');
              return (
                <li>
                  {" "}
                  <IconButton
                    aria-label="delete"
                    color="primary"
                    onClick={() => deleteNormalFiles(fileObj.name)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  {extractOriginalFileName(encodedFileName)}
                </li>
              );
            })}
        </ul>

        <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <TextArea
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="Notes"
              value={returnUpdate?.notes}
              onChange={(e) => {
                setReturnUpdate((prev) => ({
                  ...prev,
                  notes: e.target.value
                }))
              }}
            />
          </Col>
        </Row>
      </Modal>

    </div>
  );
};

export default CreditReport;
